/**
 * N2EnterprisePage — the "For managed books" B2B destination at /enterprise.
 *
 * Advisories, family offices, prop desks running $500k+ books. Audience
 * wants defensible research, published methodology, and a direct line
 * to the desk that builds the models. CTA is "Talk to us" (contact-sales),
 * not "Sign up" — this is not a self-serve product.
 */

import React from 'react';
import { N2Frame } from './N2Frame';
import { palette } from './theme';
import {
  SectionLabel, PrimaryButton, SecondaryButton, Eyebrow, PageTitle, PageLede,
  SectionHead, Body,
} from './N2Primitives';

export const N2EnterprisePage: React.FC = () => (
  <N2Frame page="enterprise">
    <Hero />
    <WhatYouGetSection />
    <ProofSection />
    <HowItWorksSection />
    <ContactCTA />
  </N2Frame>
);

const Hero: React.FC = () => (
  <section style={{
    maxWidth: 1100, margin: '0 auto', padding: '96px 32px 48px',
    textAlign: 'center',
  }}>
    <Eyebrow>For managed books</Eyebrow>
    <PageTitle italic="compliance review.">
      Research you can bring to a
    </PageTitle>
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <PageLede>
        For advisories, family offices, and prop desks running real books.
        Every model documented, every reading scored at its own horizon,
        losing periods named — not another feed of trade ideas.
      </PageLede>
    </div>
    <div style={{
      display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap',
    }}>
      <PrimaryButton to="/contact?source=n2-enterprise">Talk to us</PrimaryButton>
      <SecondaryButton to="/n2/methodology">Read the methodology</SecondaryButton>
    </div>
  </section>
);

// ---- What you get ---------------------------------------------------------

interface Capability {
  label: string;
  title: string;
  body: string;
}

const CAPABILITIES: Capability[] = [
  {
    label: 'Published methodology',
    title: 'Every model documented in plain English',
    body: 'For each model — what it measures, the framework, the regimes it works in, the regimes it fails in. Published on /n2/methodology. Cite it in a client review, no black box to explain around.',
  },
  {
    label: 'Every reading scored',
    title: 'Losses named, not skipped',
    body: 'Every reading fires publicly on the Scorecard, scored at the model\'s own horizon. Every hit rate cites its date range and observation count. Nothing about the past gets quietly restated.',
  },
  {
    label: 'Custom research on request',
    title: 'Coverage tailored to your book',
    body: 'Beyond the published models — if you need a specific instrument added, a regime overlay tuned to your risk profile, or a research question run against our chain archive, we build it. Direct line to the desk.',
  },
  {
    label: 'Dedicated support',
    title: 'The person who built it takes your call',
    body: 'Not a support queue. Named contact who knows the models, the data pipelines, and your book. Weekly check-ins during onboarding, monthly after.',
  },
];

const WhatYouGetSection: React.FC = () => (
  <section style={{
    background: palette.paper2,
    borderTop: `1px solid ${palette.line}`, borderBottom: `1px solid ${palette.line}`,
    padding: '80px 32px', marginTop: 32,
  }}>
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <SectionLabel left="What you get" right="not another signal service" />
      <div style={{ marginTop: 8, marginBottom: 40 }}>
        <SectionHead italic="reasoning.">Defensible</SectionHead>
        <Body>
          The bar is what you'd want to defend in front of a client or a
          committee: transparent method, honest results, actual support.
        </Body>
      </div>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 20,
      }} className="n2-caps">
        {CAPABILITIES.map((c) => (
          <div key={c.label} style={{
            background: palette.paper, border: `1px solid ${palette.line}`,
            borderRadius: 4, padding: '28px 28px 24px', position: 'relative',
          }}>
            <div style={{
              position: 'absolute', top: 0, left: 28, width: 28, height: 3,
              background: palette.gold,
            }} />
            <div style={{
              fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
              letterSpacing: '0.22em', textTransform: 'uppercase',
              color: palette.goldDeep, marginBottom: 12,
            }}>{c.label}</div>
            <h3 style={{
              fontFamily: 'var(--serif)', fontWeight: 500, fontSize: 22,
              lineHeight: 1.25, margin: '0 0 10px', color: palette.ink,
              letterSpacing: '-0.01em',
            }}>{c.title}</h3>
            <p style={{
              fontFamily: 'var(--sans)', fontSize: 14, lineHeight: 1.6,
              color: palette.ink2, margin: 0,
            }}>{c.body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ---- Proof — link to real published surfaces ------------------------------

const ProofSection: React.FC = () => (
  <section style={{ maxWidth: 1100, margin: '0 auto', padding: '96px 32px 0' }}>
    <SectionLabel left="See it before you ask" right="everything is public" />
    <div style={{ marginTop: 8, marginBottom: 40 }}>
      <SectionHead italic="already do.">Read what we</SectionHead>
      <Body>
        You don't need a demo. The research is public. Read a briefing, walk
        through the scorecard, check the methodology. If the shape of the
        output looks like something you'd bring to a client, come talk.
      </Body>
    </div>
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16,
    }}>
      {[
        { to: '/n2/briefings', label: 'Today\'s briefing', desc: 'Twice-daily read on regime + positioning' },
        { to: '/n2/scorecard', label: 'Public scorecard', desc: '353 published readings, losses included' },
        { to: '/n2/methodology', label: 'Methodology', desc: 'Every model, framework, and failure mode' },
      ].map((p) => (
        <a key={p.to} href={p.to} style={{
          textDecoration: 'none', color: 'inherit',
          padding: '22px 22px 20px', border: `1px solid ${palette.line}`,
          background: palette.paper, borderRadius: 4,
          display: 'block', transition: 'border-color 120ms',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.borderColor = palette.goldDeep)}
        onMouseLeave={(e) => (e.currentTarget.style.borderColor = palette.line)}>
          <div style={{
            fontFamily: 'var(--serif)', fontSize: 18, fontWeight: 500,
            color: palette.ink, marginBottom: 6,
          }}>{p.label}</div>
          <div style={{
            fontFamily: 'var(--sans)', fontSize: 13, color: palette.ink3,
          }}>{p.desc}</div>
        </a>
      ))}
    </div>
  </section>
);

// ---- How it works --------------------------------------------------------

const HowItWorksSection: React.FC = () => (
  <section style={{
    maxWidth: 1100, margin: '0 auto', padding: '96px 32px 0',
  }}>
    <SectionLabel left="How it works" right="three steps to first read" />
    <div style={{ marginTop: 40 }}>
      {[
        { step: 'I', title: 'Introductory call', body: 'You describe your book, what you\'re currently using, and what would move the needle for you. We describe what fits and what doesn\'t — honestly, including cases where we\'re not the right tool.' },
        { step: 'II', title: 'Two-week evaluation', body: 'You get live access to the research surface for your instruments — same read the paid customers get. You use it against your process, we answer questions as they come up.' },
        { step: 'III', title: 'Custom scope + engagement', body: 'If it fits, we scope out what custom work (if any) matters for your setup and put together an engagement. Priced by scope, not per-seat.' },
      ].map((s, i) => (
        <div key={s.step} style={{
          display: 'grid', gridTemplateColumns: '80px 1fr', gap: 32,
          padding: '24px 0',
          borderBottom: i < 2 ? `1px solid ${palette.line}` : 'none',
        }}>
          <div style={{
            fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 40,
            color: palette.goldDeep, textAlign: 'center', lineHeight: 1,
          }}>{s.step}</div>
          <div>
            <h3 style={{
              fontFamily: 'var(--serif)', fontWeight: 500, fontSize: 24,
              margin: '0 0 8px', color: palette.ink, letterSpacing: '-0.01em',
            }}>{s.title}</h3>
            <p style={{
              fontFamily: 'var(--sans)', fontSize: 15, lineHeight: 1.6,
              color: palette.ink2, margin: 0,
            }}>{s.body}</p>
          </div>
        </div>
      ))}
    </div>
  </section>
);

// ---- Contact CTA ---------------------------------------------------------

const ContactCTA: React.FC = () => (
  <section style={{
    maxWidth: 1100, margin: '0 auto', padding: '96px 32px 96px',
    textAlign: 'center',
  }}>
    <SectionHead italic="starts here.">The conversation</SectionHead>
    <div style={{ maxWidth: 560, margin: '0 auto 24px' }}>
      <Body>
        No sales team, no BDR chain. Direct email to the person who owns
        the models. Response within 24 hours on business days.
      </Body>
    </div>
    <PrimaryButton to="/contact?source=n2-enterprise">Talk to us</PrimaryButton>
  </section>
);

export default N2EnterprisePage;
